import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import rootReducer from "./slices";
import { initVersionCheck } from './utils/versionManager';

// Suppress ResizeObserver loop errors (harmless warning from date pickers)
// This is a known harmless warning that occurs with Flatpickr and won't appear in production

// 1. Patch ResizeObserver at the earliest possible moment
if (typeof window !== 'undefined') {
  const OriginalResizeObserver = window.ResizeObserver;

  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
}

// 2. Suppress console errors
const resizeObserverErrRegex = /ResizeObserver loop/i;
const originalConsoleError = console.error;
console.error = function(...args: any[]) {
  if (args[0] && resizeObserverErrRegex.test(args[0].toString())) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// 3. Catch ALL error events (must be done early and in capture phase)
const errorHandler = (e: ErrorEvent) => {
  if (e.message && resizeObserverErrRegex.test(e.message)) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
  }
};
window.addEventListener('error', errorHandler, { capture: true });

// 4. Hide webpack-dev-server overlay for ResizeObserver errors
const hideWebpackOverlay = () => {
  const overlay = document.querySelector('iframe[id*="webpack"]') as HTMLIFrameElement;
  if (overlay) {
    const overlayDocument = overlay.contentDocument;
    if (overlayDocument) {
      const errorContent = overlayDocument.body?.innerText || '';
      if (resizeObserverErrRegex.test(errorContent)) {
        overlay.style.display = 'none';
      }
    }
  }
};

// Watch for overlay appearing
const observer = new MutationObserver(() => {
  hideWebpackOverlay();
});
observer.observe(document.body, { childList: true, subtree: true });

// Also check periodically
setInterval(hideWebpackOverlay, 100);

const store = configureStore({ reducer: rootReducer, devTools: true });

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <Provider store={store}>
    <React.Fragment>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <App />
      </BrowserRouter>
    </React.Fragment>
  </Provider>
);

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('Service Worker registered successfully:', registration);

        // Check for updates every hour
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // Listen for service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
                console.log('New service worker activated, checking version...');
                // Trigger version check when service worker updates
                import('./utils/versionManager').then(({ checkForUpdates }) => {
                  checkForUpdates({ autoReload: true });
                });
              }
            });
          }
        });
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
      });
  });
}

// Initialize automatic version checking
// This will check for updates every 5 minutes and when the page becomes visible
console.log('Initializing version check system...');
initVersionCheck();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();